/** Shared CMS authoring metadata. Render components remain in the existing web registry. */
import type { CmsField, CmsNumberField, CmsBlockMetadata } from "./cms-schema";
import { CMS_COLLECTION_TEMPLATE_TYPES } from "./cms-schema";
import { WIDGET_SLOTS } from "./cms-widget-slots";
export const APP_DOWNLOAD_DEFAULTS = {
  eyebrow: "AERO In Your Pocket",
  title: "Take the network with you.",
  description:
    "Live fuel prices, FBO details, trip planning, and arrival alerts — on every ramp, from your phone.",
  appStoreHref: "",
  playStoreHref: "",
  image: "",
};
const EYEBROW: CmsField = {
  key: "eyebrow",
  label: "Eyebrow",
  type: "text",
  help: "Small label shown above the heading.",
};
const HEADING: CmsField = { key: "heading", label: "Heading", type: "text" };
const DESCRIPTION: CmsField = {
  key: "description",
  label: "Description",
  type: "textarea",
};

/**
 * Shared editable header copy for the "page" blocks (the full public pages
 * authored as CMS pages). Defaults are intentionally blank so each page renders
 * its built-in default eyebrow / title / description when nothing is authored.
 */
const PAGE_TITLE: CmsField = { key: "title", label: "Title", type: "text" };
const PAGE_HEADER_FIELDS: CmsField[] = [EYEBROW, PAGE_TITLE, DESCRIPTION];
const PAGE_HEADER_DEFAULTS = { eyebrow: "", title: "", description: "" };

/** Select options for the sponsorship-slot pickers (advertisement / partner). */
const SLOT_OPTIONS = Object.values(WIDGET_SLOTS).map((value) => ({
  value,
  label: value,
}));

const FOCAL_FIELDS: CmsNumberField[] = [
  {
    key: "focalX",
    label: "Horizontal focal point (%)",
    type: "number",
    min: 0,
    max: 100,
    step: 1,
    help: "0 is left,100 is right; blank uses the center.",
  },
  {
    key: "focalY",
    label: "Vertical focal point (%)",
    type: "number",
    min: 0,
    max: 100,
    step: 1,
    help: "0 is top,100 is bottom; blank uses the center.",
  },
];

export const CMS_BLOCK_METADATA: CmsBlockMetadata[] = [
  {
    type: "content_group",
    label: "Reusable section",
    category: "layout",
    description:
      "An owned section of nested blocks. Copy sections without changing a linked global.",
    fields: [{ key: "blocks", label: "Section content", type: "blocks" }],
    defaults: { blocks: [] },
  },
  {
    type: "public_collection",
    label: "Public directory collection",
    category: "entity",
    description:
      "Bounded live maintenance/manufacturer service-center or service-provider records, with an editable display template.",
    fields: [
      HEADING,
      {
        key: "source",
        label: "Public directory",
        type: "select",
        options: [
          {
            value: "maintenance",
            label: "Maintenance / manufacturer service centers",
          },
          { value: "services", label: "Service providers" },
        ],
      },
      { key: "query", label: "Search", type: "text" },
      {
        key: "manufacturer",
        label: "Manufacturer (maintenance)",
        type: "text",
      },
      { key: "category", label: "Category", type: "text" },
      {
        key: "region",
        label: "State (maintenance) / coverage (services)",
        type: "text",
      },
      {
        key: "limit",
        label: "Maximum records",
        type: "number",
        min: 1,
        max: 12,
        step: 1,
      },
      { key: "emptyText", label: "No results", type: "text" },
      {
        key: "blocks",
        label: "Repeated record template",
        type: "blocks",
        allowedTypes: CMS_COLLECTION_TEMPLATE_TYPES,
        help: "Display blocks only. Use Collection item bindings; source records are never copied into this document.",
      },
    ],
    defaults: {
      source: "maintenance",
      heading: "Service centers",
      limit: 6,
      emptyText: "No matching providers.",
      blocks: [
        {
          type: "rich_text",
          props: {
            heading: "{{item.name|Provider}}",
            paragraphs: [
              "{{item.city}} {{item.state}}",
              "{{item.description}}",
            ],
          },
        },
      ],
    },
  },
  {
    type: "hero",
    label: "Hero",
    description: "Full-bleed page header with eyebrow, title, and image.",
    category: "layout",
    fields: [
      { key: "image", label: "Background Image", type: "image" },
      EYEBROW,
      { key: "title", label: "Title", type: "text", required: true },
      DESCRIPTION,
      ...FOCAL_FIELDS,
    ],
    defaults: {
      image: "hero-airport.jpg",
      eyebrow: "AERO",
      title: "Welcome To AERO",
      description: "",
    },
  },
  {
    type: "rich_text",
    label: "Rich Text",
    description: "Heading plus one or more paragraphs of copy.",
    category: "layout",
    fields: [
      HEADING,
      {
        key: "paragraphs",
        label: "Paragraphs",
        type: "richtext",
        help: "One paragraph per entry.",
      },
    ],
    defaults: {
      heading: "",
      paragraphs: ["Add your content here."],
    },
  },
  {
    type: "image",
    label: "Image",
    description: "A single image with an optional caption.",
    category: "media",
    fields: [
      { key: "image", label: "Image", type: "image", required: true },
      { key: "alt", label: "Alt Text", type: "text" },
      { key: "caption", label: "Caption", type: "text" },
      ...FOCAL_FIELDS,
      {
        key: "crop",
        label: "Crop shape",
        type: "select",
        options: [
          { value: "natural", label: "Natural image" },
          { value: "wide", label: "Wide 16:9" },
          { value: "square", label: "Square" },
          { value: "portrait", label: "Portrait 3:4" },
        ],
      },
      {
        key: "width",
        label: "Width",
        type: "select",
        options: [
          { value: "narrow", label: "Narrow" },
          { value: "wide", label: "Wide" },
          { value: "full", label: "Full Bleed" },
        ],
      },
    ],
    defaults: { image: "", alt: "", caption: "", width: "wide" },
  },
  {
    type: "gallery",
    label: "Gallery",
    description: "A responsive grid of captioned images.",
    category: "media",
    fields: [
      EYEBROW,
      HEADING,
      {
        key: "images",
        label: "Images",
        type: "list",
        itemLabel: "Image",
        fields: [
          { key: "image", label: "Image", type: "image", required: true },
          { key: "caption", label: "Caption", type: "text" },
          { key: "alt", label: "Alt Text", type: "text" },
          ...FOCAL_FIELDS,
        ],
      },
    ],
    defaults: { eyebrow: "", heading: "", images: [] },
  },
  {
    type: "video",
    label: "Video",
    description: "An embedded YouTube/Vimeo player or a hosted video file.",
    category: "media",
    fields: [
      EYEBROW,
      HEADING,
      {
        key: "url",
        label: "Video URL",
        type: "link",
        required: true,
        help: "YouTube, Vimeo, or a direct .mp4/.webm/.ogg URL.",
      },
      { key: "caption", label: "Caption", type: "text" },
    ],
    defaults: { eyebrow: "", heading: "", url: "", caption: "" },
  },
  {
    type: "feature_cards",
    label: "Feature Cards",
    description: "A grid of icon + title + body cards.",
    category: "marketing",
    fields: [
      EYEBROW,
      HEADING,
      DESCRIPTION,
      {
        key: "columns",
        label: "Columns",
        type: "number",
        help: "2, 3, or 4.",
      },
      {
        key: "cards",
        label: "Cards",
        type: "list",
        itemLabel: "Card",
        fields: [
          {
            key: "icon",
            label: "Icon",
            type: "select",
            options: [
              { value: "plane", label: "Plane" },
              { value: "fuel", label: "Fuel" },
              { value: "shield", label: "Shield" },
              { value: "gauge", label: "Gauge" },
              { value: "star", label: "Star" },
              { value: "pin", label: "Pin" },
              { value: "building", label: "Building" },
              { value: "clock", label: "Clock" },
              { value: "award", label: "Award" },
              { value: "wrench", label: "Wrench" },
              { value: "scale", label: "Scale" },
              { value: "trending-down", label: "Trending Down" },
            ],
          },
          { key: "title", label: "Title", type: "text" },
          { key: "body", label: "Body", type: "textarea" },
          { key: "href", label: "Link", type: "link" },
        ],
      },
    ],
    defaults: {
      eyebrow: "",
      heading: "Why AERO",
      description: "",
      columns: 3,
      cards: [],
    },
  },
  {
    type: "testimonials",
    label: "Testimonials",
    description: "Quote cards with author name, role, and avatar.",
    category: "marketing",
    fields: [
      EYEBROW,
      HEADING,
      {
        key: "items",
        label: "Testimonials",
        type: "list",
        itemLabel: "Testimonial",
        fields: [
          { key: "quote", label: "Quote", type: "textarea", required: true },
          { key: "author", label: "Author", type: "text" },
          { key: "role", label: "Role", type: "text" },
          { key: "avatar", label: "Avatar", type: "image" },
        ],
      },
    ],
    defaults: { eyebrow: "", heading: "", items: [] },
  },
  {
    type: "statistics",
    label: "Statistics",
    description: "A band of headline figures with labels.",
    category: "marketing",
    fields: [
      EYEBROW,
      HEADING,
      {
        key: "stats",
        label: "Statistics",
        type: "list",
        itemLabel: "Statistic",
        fields: [
          { key: "value", label: "Value", type: "text", required: true },
          { key: "label", label: "Label", type: "text" },
        ],
      },
    ],
    defaults: { eyebrow: "", heading: "", stats: [] },
  },
  {
    type: "cta",
    label: "Call To Action",
    description: "A centered banner with copy and up to two buttons.",
    category: "marketing",
    fields: [
      EYEBROW,
      { key: "title", label: "Title", type: "text", required: true },
      DESCRIPTION,
      { key: "primaryLabel", label: "Primary Button Label", type: "text" },
      { key: "primaryHref", label: "Primary Button Link", type: "link" },
      { key: "secondaryLabel", label: "Secondary Button Label", type: "text" },
      { key: "secondaryHref", label: "Secondary Button Link", type: "link" },
      { key: "image", label: "Background Image", type: "image" },
    ],
    defaults: {
      eyebrow: "",
      title: "Ready To Get Started?",
      description: "",
      primaryLabel: "",
      primaryHref: "",
      secondaryLabel: "",
      secondaryHref: "",
      image: "",
    },
  },
  {
    type: "house_ad",
    label: "House Ad",
    description:
      "AERO house creative: 390×390 artwork or a reusable template. Publish in house-ads-square or house-ads-banner Global Content to fill unsold slots.",
    category: "marketing",
    fields: [
      {
        key: "mode",
        label: "Creative",
        type: "select",
        options: [
          { value: "template", label: "Template" },
          { value: "image", label: "Uploaded artwork" },
        ],
      },
      {
        key: "format",
        label: "Size",
        type: "select",
        options: [
          { value: "medium_rectangle", label: "Sidebar square — 390×390" },
          { value: "homepage_banner", label: "Banner — 970×250" },
        ],
      },
      {
        key: "template",
        label: "Template",
        type: "select",
        options: [
          { value: "editorial", label: "Editorial" },
          { value: "spotlight", label: "Spotlight" },
          { value: "showcase", label: "Showcase" },
        ],
      },
      {
        key: "headline",
        label: "Headline / artwork description",
        type: "text",
        required: true,
      },
      { key: "body", label: "Body", type: "textarea" },
      { key: "ctaLabel", label: "Button label", type: "text" },
      { key: "ctaUrl", label: "Destination", type: "link", required: true },
      { key: "imageUrl", label: "Artwork / template image", type: "image" },
      { key: "logoUrl", label: "Logo", type: "image" },
    ],
    defaults: {
      mode: "template",
      format: "medium_rectangle",
      template: "editorial",
      headline: "Explore AERO",
      body: "Plan your next arrival.",
      ctaLabel: "Explore",
      ctaUrl: "/map",
      imageUrl: "",
      logoUrl: "",
    },
  },
  {
    type: "app_download",
    label: "App Download",
    description:
      "A branded callout promoting the AERO mobile app with App Store / Google Play badges.",
    category: "marketing",
    fields: [
      EYEBROW,
      { key: "title", label: "Title", type: "text", required: true },
      DESCRIPTION,
      { key: "appStoreHref", label: "App Store Link", type: "link" },
      { key: "playStoreHref", label: "Google Play Link", type: "link" },
      { key: "image", label: "App Screenshot (optional)", type: "image" },
    ],
    defaults: APP_DOWNLOAD_DEFAULTS,
  },
  {
    type: "faq",
    label: "FAQ",
    description: "An accordion of question and answer pairs.",
    category: "marketing",
    fields: [
      EYEBROW,
      HEADING,
      {
        key: "items",
        label: "Questions",
        type: "list",
        itemLabel: "Question",
        fields: [
          { key: "question", label: "Question", type: "text", required: true },
          { key: "answer", label: "Answer", type: "textarea", required: true },
        ],
      },
    ],
    defaults: { eyebrow: "", heading: "Frequently Asked Questions", items: [] },
  },
  {
    type: "newsletter_signup",
    label: "Newsletter Signup",
    description: "A waitlist signup form (name, role, email, consent).",
    category: "engagement",
    fields: [
      EYEBROW,
      HEADING,
      DESCRIPTION,
      { key: "buttonLabel", label: "Button Label", type: "text" },
      { key: "consentLabel", label: "Consent Label", type: "text" },
      { key: "successMessage", label: "Success Message", type: "text" },
    ],
    defaults: {
      eyebrow: "",
      heading: "Join The Waitlist",
      description: "",
      buttonLabel: "Request Access",
      consentLabel: "I Agree To Receive Launch And Product Updates From AERO.",
      successMessage: "You're On The List. We'll Be In Touch.",
    },
  },
  {
    type: "map",
    label: "Map",
    description: "An interactive map of FBO locations across the network.",
    category: "engagement",
    fields: [
      EYEBROW,
      HEADING,
      DESCRIPTION,
      {
        key: "state",
        label: "State Filter",
        type: "text",
        help: "Two-letter state code to limit pins (optional).",
      },
      {
        key: "height",
        label: "Height (px)",
        type: "number",
        help: "240–800.",
      },
    ],
    defaults: {
      eyebrow: "",
      heading: "",
      description: "",
      state: "",
      height: 460,
    },
  },
  {
    type: "fbo_spotlight",
    label: "FBO Spotlight",
    description: "An editorial featured card for one FBO (live data).",
    category: "entity",
    fields: [
      {
        key: "slug",
        label: "FBO",
        type: "entity",
        entity: "fbo",
        required: true,
      },
      EYEBROW,
      { key: "ctaLabel", label: "Button Label", type: "text" },
      {
        key: "blurb",
        label: "Custom Blurb",
        type: "textarea",
        help: "Overrides the FBO's own description.",
      },
    ],
    defaults: {
      slug: "",
      eyebrow: "Featured FBO",
      ctaLabel: "View FBO",
      blurb: "",
    },
  },
  {
    type: "airport_spotlight",
    label: "Airport Spotlight",
    description: "An editorial featured card for one airport (live data).",
    category: "entity",
    fields: [
      {
        key: "code",
        label: "Airport",
        type: "entity",
        entity: "airport",
        required: true,
      },
      EYEBROW,
      { key: "ctaLabel", label: "Button Label", type: "text" },
      { key: "blurb", label: "Custom Blurb", type: "textarea" },
    ],
    defaults: {
      code: "",
      eyebrow: "Featured Airport",
      ctaLabel: "View Airport",
      blurb: "",
    },
  },
  {
    type: "flight_school_spotlight",
    label: "Flight School Spotlight",
    description:
      "An editorial featured card for one flight school (live data).",
    category: "entity",
    fields: [
      {
        key: "slug",
        label: "Flight School",
        type: "entity",
        entity: "flight_school",
        required: true,
      },
      EYEBROW,
      { key: "ctaLabel", label: "Button Label", type: "text" },
      { key: "blurb", label: "Custom Blurb", type: "textarea" },
    ],
    defaults: {
      slug: "",
      eyebrow: "Featured Flight School",
      ctaLabel: "View Flight School",
      blurb: "",
    },
  },
  {
    type: "spotlight",
    label: "Spotlight",
    description:
      "A hand-authored featured card for any subject (MRO, aircraft sales, custom).",
    category: "entity",
    fields: [
      EYEBROW,
      { key: "name", label: "Title", type: "text", required: true },
      { key: "location", label: "Location / Subtitle", type: "text" },
      { key: "image", label: "Image", type: "image" },
      { key: "blurb", label: "Blurb", type: "textarea" },
      { key: "href", label: "Link", type: "link" },
      { key: "ctaLabel", label: "Button Label", type: "text" },
    ],
    defaults: {
      eyebrow: "",
      name: "",
      location: "",
      image: "",
      blurb: "",
      href: "",
      ctaLabel: "Learn More",
    },
  },
  {
    type: "event_promotion",
    label: "Event Promotion",
    description: "An editorial featured card for one event (live data).",
    category: "entity",
    fields: [
      {
        key: "slug",
        label: "Event",
        type: "entity",
        entity: "event",
        required: true,
      },
      EYEBROW,
      { key: "ctaLabel", label: "Button Label", type: "text" },
      { key: "blurb", label: "Custom Blurb", type: "textarea" },
    ],
    defaults: {
      slug: "",
      eyebrow: "Featured Event",
      ctaLabel: "View Event",
      blurb: "",
    },
  },
  {
    type: "advertisement",
    label: "Advertisement",
    description: "Surfaces a sponsorship widget from the placement engine.",
    category: "engagement",
    fields: [
      {
        key: "slot",
        label: "Slot",
        type: "select",
        options: SLOT_OPTIONS,
        help: "Which named sponsorship slot to render.",
      },
      EYEBROW,
      HEADING,
    ],
    defaults: {
      slot: WIDGET_SLOTS.homeBottom,
      eyebrow: "",
      heading: "",
    },
  },
  {
    type: "partner_promotion",
    label: "Partner Promotion",
    description:
      "A partner-branded sponsorship surface from the placement engine.",
    category: "engagement",
    fields: [
      {
        key: "slot",
        label: "Slot",
        type: "select",
        options: SLOT_OPTIONS,
        help: "Which named sponsorship slot to render.",
      },
      EYEBROW,
      HEADING,
    ],
    defaults: {
      slot: WIDGET_SLOTS.fboDetailPartner,
      eyebrow: "In Partnership With",
      heading: "",
    },
  },
  {
    type: "search_hero",
    label: "Search Hero",
    description:
      "Full-bleed hero with the universal search bar, CTAs, and a live platform stat band.",
    category: "layout",
    fields: [
      { key: "image", label: "Background Image", type: "image" },
      { key: "badge", label: "Badge", type: "text" },
      { key: "title", label: "Title", type: "text" },
      {
        key: "titleHighlight",
        label: "Title (Highlighted Line)",
        type: "text",
        help: "Second headline line rendered as a gold gradient.",
      },
      DESCRIPTION,
      { key: "showSearch", label: "Show Search Bar", type: "boolean" },
      { key: "showStats", label: "Show Platform Stats", type: "boolean" },
      { key: "primaryLabel", label: "Primary Button Label", type: "text" },
      { key: "primaryHref", label: "Primary Button Link", type: "link" },
      { key: "secondaryLabel", label: "Secondary Button Label", type: "text" },
      { key: "secondaryHref", label: "Secondary Button Link", type: "link" },
    ],
    defaults: {
      image: "jet_sky_2.jpg",
      badge: "Business Aviation Network",
      title: "Find The Right FBO For",
      titleHighlight: "Every Mission",
      description:
        "Compare fuel pricing, amenities, and service across the US business aviation network.",
      showSearch: true,
      showStats: true,
      primaryLabel: "",
      primaryHref: "",
      secondaryLabel: "",
      secondaryHref: "",
    },
  },
  {
    type: "featured_fbos",
    label: "Featured FBOs",
    description: "A live carousel of premier FBOs across the network.",
    category: "entity",
    fields: [
      EYEBROW,
      { key: "title", label: "Title", type: "text" },
      DESCRIPTION,
      { key: "ctaLabel", label: "Button Label", type: "text" },
      { key: "ctaHref", label: "Button Link", type: "link" },
      {
        key: "limit",
        label: "Max Cards",
        type: "number",
        help: "How many FBOs to show (default 8).",
      },
    ],
    defaults: {
      eyebrow: "Hand-picked",
      title: "Featured FBOs",
      description:
        "Premier facilities delivering exceptional service across the network.",
      ctaLabel: "All FBOs",
      ctaHref: "/fbos",
      limit: 8,
    },
  },
  {
    type: "popular_airports",
    label: "Popular Airports",
    description: "A live carousel of the busiest business aviation gateways.",
    category: "entity",
    fields: [
      EYEBROW,
      { key: "title", label: "Title", type: "text" },
      DESCRIPTION,
      { key: "ctaLabel", label: "Button Label", type: "text" },
      { key: "ctaHref", label: "Button Link", type: "link" },
      {
        key: "limit",
        label: "Max Cards",
        type: "number",
        help: "How many airports to show (default 8).",
      },
    ],
    defaults: {
      eyebrow: "Top destinations",
      title: "Popular Airports",
      description:
        "The busiest business aviation gateways, ranked by facility coverage.",
      ctaLabel: "All Airports",
      ctaHref: "/airports",
      limit: 8,
    },
  },
  {
    type: "cheapest_fuel",
    label: "Cheapest Fuel",
    description: "A live ranked list of the lowest current Jet-A prices.",
    category: "entity",
    fields: [
      EYEBROW,
      { key: "title", label: "Title", type: "text" },
      DESCRIPTION,
      { key: "ctaLabel", label: "Button Label", type: "text" },
      { key: "ctaHref", label: "Button Link", type: "link" },
      {
        key: "limit",
        label: "Max Rows",
        type: "number",
        help: "How many FBOs to list (default 6).",
      },
    ],
    defaults: {
      eyebrow: "Best value",
      title: "Lowest Jet-A Right Now",
      description:
        "Sorted by current posted price. Pricing updates continuously across the network.",
      ctaLabel: "View Fuel Prices",
      ctaHref: "/fuel-prices",
      limit: 6,
    },
  },
  {
    type: "experiences",
    label: "Experiences",
    description:
      "A live featured event plus a carousel of upcoming experiences.",
    category: "entity",
    fields: [
      EYEBROW,
      { key: "title", label: "Title", type: "text" },
      DESCRIPTION,
      { key: "ctaLabel", label: "Button Label", type: "text" },
      { key: "ctaHref", label: "Button Link", type: "link" },
    ],
    defaults: {
      eyebrow: "Discover What’s Happening",
      title: "Experiences",
      description:
        "Event-specific fuel pricing, fees, and availability across every participating airport and FBO.",
      ctaLabel: "All Experiences",
      ctaHref: "/experiences",
    },
  },
  {
    type: "aero_score",
    label: "AERO Score",
    description:
      "The AERO Score explainer with a live sample score card from the scoring engine.",
    category: "marketing",
    fields: [
      { key: "badge", label: "Badge", type: "text" },
      { key: "title", label: "Title", type: "text" },
      DESCRIPTION,
      {
        key: "points",
        label: "Talking Points",
        type: "list",
        itemLabel: "Point",
        max: 3,
        fields: [
          { key: "title", label: "Title", type: "text" },
          { key: "body", label: "Body", type: "textarea" },
        ],
      },
      { key: "primaryLabel", label: "Primary Button Label", type: "text" },
      { key: "primaryHref", label: "Primary Button Link", type: "link" },
      { key: "secondaryLabel", label: "Secondary Button Label", type: "text" },
      { key: "secondaryHref", label: "Secondary Button Link", type: "link" },
    ],
    defaults: {
      badge: "The AERO Difference",
      title: "The AERO Score",
      description:
        "One balanced rating across fuel value, safety, service, experience, responsiveness, and profile completeness — so you can weigh real value against overall capability at a glance.",
      points: [],
      primaryLabel: "",
      primaryHref: "",
      secondaryLabel: "",
      secondaryHref: "",
    },
  },
  {
    type: "announcement",
    label: "Announcement",
    description: "A slim centered promo strip with a short message and a link.",
    category: "marketing",
    fields: [
      { key: "text", label: "Message", type: "text" },
      { key: "linkLabel", label: "Link Label", type: "text" },
      { key: "linkHref", label: "Link", type: "link" },
    ],
    defaults: {
      text: "Manage An FBO Or Airport?",
      linkLabel: "Get Listed",
      linkHref: "/list-your-fbo",
    },
  },
  {
    type: "ad_leaderboard",
    label: "Ad Leaderboard",
    description: "Surfaces a booked leaderboard ad from the paid ad system.",
    category: "engagement",
    fields: [
      {
        key: "slot",
        label: "Slot",
        type: "select",
        options: [
          { value: "homepage_top", label: "Homepage Top" },
          { value: "homepage_bottom", label: "Homepage Bottom" },
        ],
      },
    ],
    defaults: { slot: "homepage_top" },
  },
  {
    type: "homepage_discovery",
    label: "Homepage Discovery",
    description:
      "A connected discovery grid with audience paths and contextual journeys.",
    category: "marketing",
    fields: [
      EYEBROW,
      { key: "title", label: "Title", type: "text" },
      {
        key: "titleHighlight",
        label: "Title Highlight",
        type: "text",
      },
      { key: "ctaLabel", label: "Button Label", type: "text" },
      { key: "ctaHref", label: "Button Link", type: "link" },
      {
        key: "features",
        label: "Features",
        type: "list",
        itemLabel: "Feature",
        fields: [
          { key: "title", label: "Title", type: "text", required: true },
          { key: "body", label: "Description", type: "textarea" },
          { key: "href", label: "Link", type: "link", required: true },
          {
            key: "icon",
            label: "Icon",
            type: "select",
            options: [
              { value: "search", label: "Search" },
              { value: "plane", label: "Plane" },
              { value: "compare", label: "Compare" },
              { value: "compass", label: "Compass" },
              { value: "building", label: "Building" },
              { value: "advertise", label: "Advertise" },
            ],
          },
        ],
      },
      {
        key: "audiences",
        label: "Audience Paths",
        type: "list",
        itemLabel: "Audience",
        fields: [
          { key: "title", label: "Title", type: "text", required: true },
          { key: "href", label: "Link", type: "link", required: true },
        ],
      },
      {
        key: "journeys",
        label: "Connected Journeys",
        type: "list",
        itemLabel: "Journey",
        fields: [
          { key: "title", label: "Title", type: "text", required: true },
          { key: "body", label: "Description", type: "textarea" },
          { key: "href", label: "Link", type: "link", required: true },
          {
            key: "icon",
            label: "Icon",
            type: "select",
            options: [
              { value: "airport", label: "Airport" },
              { value: "fbo", label: "FBO" },
              { value: "plane", label: "Plane" },
              { value: "building", label: "Building" },
            ],
          },
        ],
      },
    ],
    defaults: {
      eyebrow: "More Than a Directory",
      title: "Explore Everything",
      titleHighlight: "AERO Offers.",
      ctaLabel: "Explore Aviation Services",
      ctaHref: "/services",
      features: [],
      audiences: [],
      journeys: [],
    },
  },
  {
    type: "global_ref",
    label: "Global Content",
    description:
      "Embeds a reusable global content item by key; updates everywhere when re-published.",
    category: "layout",
    fields: [
      {
        key: "globalKey",
        label: "Global Content",
        type: "global",
        required: true,
        help: "Pick the published global content item to embed.",
      },
    ],
    defaults: { globalKey: "" },
  },
  {
    type: "tabbed_container",
    label: "Tabbed Container",
    description:
      "A set of tabs, each holding its own nested block list the visitor can switch between.",
    category: "layout",
    fields: [
      {
        key: "tabs",
        label: "Tabs",
        type: "tabs",
        itemLabel: "Tab",
        help: "Each tab has a label and its own block list.",
      },
    ],
    defaults: {
      tabs: [
        { label: "Tab One", blocks: [] },
        { label: "Tab Two", blocks: [] },
      ],
    },
  },
  // FBO Detail Template — entity-bound tab-body blocks. Each paints one tab of
  // the FBO detail page, reading the ambient FBO via the entity-context layer.
  // They carry no editable fields: their content is the live FBO record, so the
  // author only positions / shows-hides / reorders them within the template's
  // tabbed container.
  {
    type: "fbo_fuel",
    label: "FBO Fuel Pricing",
    description:
      "FBO fuel pricing, history chart, and fuel details (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  {
    type: "fbo_amenities",
    label: "FBO Amenities & Services",
    description: "FBO amenities and services grid (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  {
    type: "fbo_gallery",
    label: "FBO Gallery",
    description:
      "FBO photo gallery (live data; hidden when there are no extra photos).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  {
    type: "fbo_hours",
    label: "FBO Hours",
    description:
      "FBO facility and fueling hours, plus after-hours callout (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  {
    type: "fbo_hangar",
    label: "FBO Hangars",
    description:
      "FBO hangar availability and individual hangar listings (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  {
    type: "fbo_fees",
    label: "FBO Fees",
    description: "FBO fee schedule (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  {
    type: "fbo_staff",
    label: "FBO Staff",
    description: "FBO staff directory (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  {
    type: "fbo_reviews",
    label: "FBO Reviews",
    description: "FBO reviews and the review submission form (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  {
    type: "fbo_flight_school",
    label: "FBO Flight School",
    description: "Affiliated flight schools and training CTAs (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  {
    type: "fbo_facility",
    label: "FBO FAA Facility",
    description: "FAA facility record for the FBO's airport (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  {
    type: "fbo_notams",
    label: "FBO NOTAMs",
    description: "NOTAMs for the FBO's airport (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  {
    type: "fbo_documents",
    label: "FBO Documents",
    description: "FBO and airport document attachments (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  // Airport detail template entity blocks (CMS Phase 3). Each paints one tab body
  // of the airport detail page from the ambient airport entity (live data).
  {
    type: "airport_fbos",
    label: "Airport FBOs",
    description: "FBOs located at the airport (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  {
    type: "airport_fees",
    label: "Airport Fees",
    description:
      "Verified airport-wide fees and the pilot reporting action (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  {
    type: "airport_flight_schools",
    label: "Airport Flight Schools",
    description: "Flight schools located at the airport (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  {
    type: "airport_runways",
    label: "Airport Runways",
    description: "Airport runways with FAA enrichment (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  {
    type: "airport_info",
    label: "Airport Comms & Info",
    description: "Airport frequencies, ARTCC, FSS, and remarks (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  {
    type: "airport_facility",
    label: "Airport FAA Facility",
    description: "FAA facility record for the airport (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  {
    type: "airport_notams",
    label: "Airport NOTAMs",
    description: "NOTAMs for the airport (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  {
    type: "airport_documents",
    label: "Airport Documents",
    description: "Airport document attachments and owner managers (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  // Flight-school detail template entity blocks (CMS Phase 3). Each paints one
  // tab body of the flight-school detail page from the ambient school entity.
  {
    type: "school_programs",
    label: "School Programs",
    description: "Training programs and curriculum (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  {
    type: "school_aircraft",
    label: "School Aircraft Fleet",
    description: "Training aircraft fleet (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  {
    type: "school_instructors",
    label: "School Instructors",
    description: "Flight instructors and certifications (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  {
    type: "school_about",
    label: "School About",
    description:
      "Training types, accreditations, financing, home airport (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  {
    type: "school_gallery",
    label: "School Gallery",
    description: "Photo gallery (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  {
    type: "school_reviews",
    label: "School Reviews",
    description: "Reviews and the review form (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  // Maintenance detail template entity blocks (CMS Phase 3). Each paints one
  // tab body of the maintenance detail page from the ambient provider entity.
  {
    type: "maintenance_capabilities",
    label: "Maintenance Capabilities",
    description: "Disciplines, capabilities, and where they work (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  {
    type: "maintenance_coverage",
    label: "Maintenance Aircraft Coverage",
    description: "Aircraft, engine, and avionics coverage (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  {
    type: "maintenance_certifications",
    label: "Maintenance Certifications",
    description: "Certifications and authorizations (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  {
    type: "maintenance_about",
    label: "Maintenance About",
    description: "Home airport (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  {
    type: "maintenance_gallery",
    label: "Maintenance Gallery",
    description: "Photo gallery (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  {
    type: "maintenance_reviews",
    label: "Maintenance Reviews",
    description: "Reviews and the review form (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  // Services (provider) detail template entity blocks (CMS Phase 3). Each paints
  // one tab body of the /services/:slug provider detail page from the ambient
  // provider entity.
  {
    type: "services_services",
    label: "Provider Services",
    description: "Service categories and capabilities (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  {
    type: "services_coverage",
    label: "Provider Coverage",
    description: "Geographic coverage (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  {
    type: "services_aircraft",
    label: "Provider Aircraft",
    description: "Aircraft categories served (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  {
    type: "services_about",
    label: "Provider About",
    description: "Certifications and provider details (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  {
    type: "services_gallery",
    label: "Provider Gallery",
    description: "Photo gallery (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  {
    type: "services_reviews",
    label: "Provider Reviews",
    description: "Reviews and the review form (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  {
    type: "experience_airports",
    label: "Experience Airports",
    description: "Recommended airports band (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  {
    type: "experience_fbos",
    label: "Experience FBOs",
    description: "Participating FBOs band (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  {
    type: "experience_fee_comparison",
    label: "Experience Fee Comparison",
    description: "Event fee comparison table band (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  {
    type: "experience_availability",
    label: "Experience Availability",
    description: "Availability dashboard band (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  {
    type: "experience_ops_notices",
    label: "Experience Operational Notices",
    description: "Operational notices band (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  {
    type: "experience_local",
    label: "Experience Local Info",
    description: "Hotels, transport, and local area band (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  {
    type: "experience_notams",
    label: "Experience NOTAMs",
    description: "NOTAMs band for participating airports (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  {
    type: "experience_faqs",
    label: "Experience FAQs",
    description: "Frequently asked questions band (live data).",
    category: "entity",
    fields: [],
    defaults: {},
  },
  {
    type: "plans_pricing",
    label: "Plans Pricing Tool",
    description:
      "The live subscription-pricing tool: listing-type tabs, term selector, plan cards, comparison table, and checkout.",
    category: "marketing",
    fields: [],
    defaults: {},
  },
  {
    type: "advertise_tool",
    label: "Advertise Tool",
    description:
      "The full advertising surface: placement pricing, the booking builder, and the request-a-quote form.",
    category: "marketing",
    fields: [],
    defaults: {},
  },
  {
    type: "build_plan_tool",
    label: "Build Plan Tool",
    description:
      "The custom multi-listing plan builder (auth-gated) that bundles every managed listing onto one shared term.",
    category: "marketing",
    fields: [],
    defaults: {},
  },
  {
    type: "airports_directory",
    label: "Airports Directory",
    description:
      "The full airports page: hero search, state/region filters, and the live airport results grid.",
    category: "marketing",
    fields: PAGE_HEADER_FIELDS,
    defaults: PAGE_HEADER_DEFAULTS,
  },
  {
    type: "fbos_directory",
    label: "FBOs Directory",
    description:
      "The full FBO page: hero search, amenity/service filters, sort, and the live FBO results grid.",
    category: "marketing",
    fields: PAGE_HEADER_FIELDS,
    defaults: PAGE_HEADER_DEFAULTS,
  },
  {
    type: "flight_schools_directory",
    label: "Flight Schools Directory",
    description:
      "The full flight schools page: hero search, program filters, sort, and the live results grid.",
    category: "marketing",
    fields: PAGE_HEADER_FIELDS,
    defaults: PAGE_HEADER_DEFAULTS,
  },
  {
    type: "services_directory",
    label: "Services Directory",
    description:
      "The full services marketplace page: hero search, category/coverage filters, and the live provider grid.",
    category: "marketing",
    fields: PAGE_HEADER_FIELDS,
    defaults: PAGE_HEADER_DEFAULTS,
  },
  {
    type: "maintenance_directory",
    label: "Maintenance Directory",
    description:
      "The full maintenance directory page: hero search, discipline filters, and the live provider grid.",
    category: "marketing",
    fields: PAGE_HEADER_FIELDS,
    defaults: PAGE_HEADER_DEFAULTS,
  },
  {
    type: "experiences_directory",
    label: "Experiences Directory",
    description:
      "The full experiences page: hero, stats, and the live aviation-events results grid.",
    category: "marketing",
    fields: PAGE_HEADER_FIELDS,
    defaults: PAGE_HEADER_DEFAULTS,
  },
  {
    type: "hangar_directory",
    label: "Hangar Requests Page",
    description:
      "The full hangar requests page: the request + waitlist tabs and private submission forms.",
    category: "marketing",
    fields: PAGE_HEADER_FIELDS,
    defaults: PAGE_HEADER_DEFAULTS,
  },
  {
    type: "network_map",
    label: "Network Map Page",
    description:
      "The full interactive network map: FBO / flight-school dataset toggle, filters, and live pins.",
    category: "marketing",
    fields: PAGE_HEADER_FIELDS,
    defaults: PAGE_HEADER_DEFAULTS,
  },
  {
    type: "compare_tool",
    label: "Compare Tool Page",
    description:
      "The full FBO comparison page: side-by-side fuel pricing, ratings, amenities, and services.",
    category: "marketing",
    fields: PAGE_HEADER_FIELDS,
    defaults: PAGE_HEADER_DEFAULTS,
  },
  {
    type: "get_listed_form",
    label: "Get Listed Form",
    description:
      "The full get-listed page: the airport / FBO listing-submission form that routes to the team.",
    category: "marketing",
    fields: PAGE_HEADER_FIELDS,
    defaults: PAGE_HEADER_DEFAULTS,
  },
  {
    type: "privacy_center",
    label: "Privacy Center Tool",
    description:
      "The interactive privacy tools: the cookie-preferences control and the live published privacy-policy grid. Pair with copy blocks for the header, CTAs, and legal text.",
    category: "marketing",
    fields: [],
    defaults: {},
  },
  {
    type: "trust_center",
    label: "Trust Center Tool",
    description:
      "The interactive trust tools: the live published trust-document grid. Pair with copy blocks for the header, security pillars, and legal text.",
    category: "marketing",
    fields: [],
    defaults: {},
  },
  {
    type: "status_page",
    label: "Status Page",
    description:
      "The full platform status page: live operational status and uptime history from health monitoring.",
    category: "marketing",
    fields: PAGE_HEADER_FIELDS,
    defaults: PAGE_HEADER_DEFAULTS,
  },
];
